import React, { useEffect, useState } from 'react';
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, TextField, Autocomplete, Box, IconButton, 
    Typography, Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MenuItem from '@mui/material/MenuItem';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { API_BASE_URL } from '../../../App';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

const ClubhouseBookingModal = ({ show, onHide, booking = null, refresh }) => {
    const [date, setDate] = useState(dayjs());
    const [startTime, setStartTime] = useState('');
    const [notes, setNotes] = useState('');
    const [rentedBy, setRentedBy] = useState(null);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Generate time options: 08:00 to 23:30 in 30min steps
    const timeOptions = [];
    for (let h = 8; h < 21; h++) {
        timeOptions.push(`${String(h).padStart(2, '0')}:00`);
        if (h < 21) timeOptions.push(`${String(h).padStart(2, '0')}:30`);
    }
    timeOptions.push('21:00');

    useEffect(() => {
        if (show) {
            axios.get(`${API_BASE_URL}/api/activePlayers`)
                .then(res => {
                    const mappedPlayers = res.data.map(p => ({
                        ...p,
                        _id: p.id,
                        label: p.name || p.email || "Unbekannt"
                    }));
                    setPlayers(mappedPlayers);
                })
                .catch(err => console.error("Error fetching players:", err));
        }
    }, [show]);

    useEffect(() => {
        if (show) {
            if (booking) {
                setDate(dayjs(booking.date));
                setNotes(booking.notes || '');
                if (booking.startTime) {
                    setStartTime(booking.startTime);
                } else {
                    setStartTime('');
                }

                if (booking.rentedBy) {
                    setRentedBy({
                        ...booking.rentedBy,
                        _id: booking.rentedBy._id,
                        label: `${booking.rentedBy.firstName} ${booking.rentedBy.lastName}`
                    });
                }
            } else {
                setDate(dayjs());
                setNotes('');
                setStartTime('');
                setRentedBy(null);
            }
        }
    }, [booking, show]);

    const handleSubmit = async () => {
        if (!date || !rentedBy) {
            toast.warn("Bitte Datum und Mieter angeben.");
            return;
        }

        setLoading(true);
        const payload = {
            date: date.toDate(),
            rentedBy: rentedBy._id,
            notes,
            startTime: startTime || null
        };

        try {
            if (booking) {
                await axios.put(`${API_BASE_URL}/api/clubhouse/${booking._id}`, payload, { withCredentials: true });
                toast.success("Buchung aktualisiert");
            } else {
                await axios.post(`${API_BASE_URL}/api/clubhouse`, payload, { withCredentials: true });
                toast.success("Buchung erstellt");
            }
            refresh();
            onHide();
        } catch (error) {
            console.error("Error saving clubhouse booking:", error);
            toast.error("Fehler beim Speichern: " + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={show} onClose={onHide} fullWidth maxWidth="sm">
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', 
                color: 'white', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                px: 3,
                py: 2
            }}>
                <Typography variant="h6" component="div" fontWeight="bold">
                    {booking ? 'Buchung bearbeiten' : 'Neue Clubheim-Buchung'}
                </Typography>
                <IconButton onClick={onHide} sx={{ color: 'white' }} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, pt: 4 }}>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <DatePicker
                            label="Datum"
                            value={date}
                            onChange={(newDate) => setDate(newDate)}
                            slotProps={{ 
                                textField: { 
                                    fullWidth: true, 
                                    size: 'small'
                                } 
                            }}
                        />
                        <TextField
                            select
                            label="Uhrzeit (optional)"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value=""><em>Keine Angabe</em></MenuItem>
                            {timeOptions.map(t => (
                                <MenuItem key={t} value={t}>{t} Uhr</MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    
                    <Autocomplete
                        options={players}
                        getOptionLabel={(option) => option.label || ""}
                        value={rentedBy}
                        onChange={(event, newValue) => setRentedBy(newValue)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Gemietet von" 
                                size="small" 
                                required
                            />
                        )}
                        renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            return (
                                <Box component="li" key={option._id} {...otherProps} sx={{ fontSize: '0.875rem' }}>
                                    {option.label}
                                </Box>
                            );
                        }}
                    />

                    <TextField
                        label="Notizen / Informationen"
                        multiline
                        rows={3}
                        fullWidth
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Z.B. Geburtstag Müller oder Veranstaltungshinweis"
                    />
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f9f9f9' }}>
                <Button onClick={onHide} color="inherit">Abbrechen</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ px: 4, fontWeight: 'bold' }}
                >
                    {loading ? 'Speichern...' : 'Speichern'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClubhouseBookingModal;
